import { useFounderPedigree } from "../hooks/use-intelligence-queries.ts";
import { exportCsv } from "../lib/csv-export.ts";
import type { FounderPedigreeBandRow } from "../types/index.ts";

function formatPct(n: number): string {
  return `${n.toFixed(1)}%`;
}

export function FounderPedigreeConcentrationView() {
  const { data, isLoading, isError } = useFounderPedigree();

  if (isLoading && !data) return <div className="intel-loading">Loading founder pedigree…</div>;
  if (isError && !data) return <div className="intel-error">Failed to load founder pedigree</div>;
  if (!data) return null;

  const { byScoreBand, topProjects } = data;

  const handleExport = () => {
    const rows: (string | number)[][] = byScoreBand.map((r: FounderPedigreeBandRow) => [
      r.band,
      r.totalProjects,
      r.withPedigree,
      r.tier1Count,
      formatPct(r.tier1Pct),
    ]);
    rows.push([
      `Top ${topProjects.topN}`,
      topProjects.totalProjects,
      topProjects.withPedigree,
      topProjects.tier1Count,
      formatPct(topProjects.tier1Pct),
    ]);
    exportCsv(
      "intelligence-founder-pedigree.csv",
      ["Segment", "Total projects", "With founder data", "Tier-1 founders", "Tier-1 share"],
      rows
    );
  };

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <div className="intel-section__header-row">
          <div>
            <h3 className="intel-section__title">Founder pedigree concentration</h3>
            <p className="intel-section__subtitle">
              Share of projects with a <strong>tier-1 founder</strong> (ex-Compound / Coinbase /
              Jump / Uniswap / a16z-portfolio etc.) across each Intelligence score band, and across
              the top {topProjects.topN} projects by alpha score. Tier-1 share is computed against
              the subset with founder data — projects without pedigree info are excluded from the
              denominator (unknowns, not negatives).
            </p>
          </div>
          <button
            type="button"
            className="intel-csv-btn"
            onClick={handleExport}
            aria-label="Download founder pedigree table as CSV"
          >
            Download CSV
          </button>
        </div>
      </header>

      <div className="intel-pedigree-hero">
        <div className="intel-pedigree-hero__label">
          Top {topProjects.topN} by alpha score · tier-1 share
        </div>
        {topProjects.withPedigree >= 3 ? (
          <>
            <div className="intel-pedigree-hero__value">{formatPct(topProjects.tier1Pct)}</div>
            <div className="intel-pedigree-hero__meta">
              {topProjects.tier1Count} of {topProjects.withPedigree} with founder data
              {topProjects.withPedigree < topProjects.totalProjects && (
                <span className="intel-pedigree-hero__missing">
                  {" "}
                  ({topProjects.totalProjects - topProjects.withPedigree} missing data)
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="intel-pedigree-hero__meta">
            Low sample — {topProjects.withPedigree} of {topProjects.totalProjects} top projects have
            founder data.
          </div>
        )}
      </div>

      <div className="intel-pedigree-wrap">
        <table className="intel-pedigree">
          <thead>
            <tr>
              <th>Score band</th>
              <th className="intel-pedigree__num-head">Projects</th>
              <th className="intel-pedigree__num-head">With data</th>
              <th className="intel-pedigree__num-head">Tier-1</th>
              <th className="intel-pedigree__num-head">Tier-1 share</th>
              <th className="intel-pedigree__bar-head">Concentration</th>
            </tr>
          </thead>
          <tbody>
            {byScoreBand.map((row) => (
              <tr key={row.band}>
                <td className="intel-pedigree__band">{row.band}</td>
                <td className="intel-pedigree__num">{row.totalProjects}</td>
                <td className="intel-pedigree__num">{row.withPedigree}</td>
                <td className="intel-pedigree__num">{row.tier1Count}</td>
                <td className="intel-pedigree__num intel-pedigree__pct">
                  {row.withPedigree >= 3 ? formatPct(row.tier1Pct) : "—"}
                </td>
                <td className="intel-pedigree__bar-cell">
                  {row.withPedigree >= 3 ? (
                    <div
                      className="intel-pedigree__bar"
                      style={{ width: `${Math.min(100, row.tier1Pct)}%` }}
                      role="progressbar"
                      aria-valuenow={row.tier1Pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${formatPct(row.tier1Pct)} tier-1 share in ${row.band}`}
                    />
                  ) : (
                    <span className="intel-pedigree__low-sample">low sample</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
